

import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";

interface PrintableLedgerProps {
  client: {
    name: string;
    email?: string;
    phone?: string;
  };
  entries: Array<{
    date: string;
    type: "invoice" | "payment";
    description: string;
    debit: number | string;
    credit: number | string;
  }>;
  summary: {
    total_invoiced: number;
    total_paid: number;
    current_balance: number;
  };
  fromDate?: string;
  toDate?: string;
  generatedAt?: string;
  businessInfo: {
    name: string;
    logoUrl?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
}

export default function PrintableLedger({
  client,
  entries,
  summary,
  fromDate = "Beginning",
  toDate = "Now",
  generatedAt = new Date().toLocaleDateString("en-IN"),
  businessInfo,
}: PrintableLedgerProps) {
  return (
    <div id="print-ledger-container" className="print-container hidden print:block">
      <div className="print-page">
        {/* Header with Gold Accent */}
        <div className="print-header-accent" />
        
        {/* Business Header */}
        <div className="print-header">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4">
              {businessInfo.logoUrl && (
                <img 
                  src={getImageUrl(businessInfo.logoUrl)} 
                  alt="Logo" 
                  className="w-20 h-20 object-contain print-logo"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold text-stone-900 mb-1 tracking-tight">
                  {businessInfo.name.toUpperCase()}
                </h1>
                <p className="text-sm text-amber-600 font-semibold tracking-wide mb-3">
                  EVENT MANAGEMENT
                </p>
                <div className="text-xs text-stone-600 leading-relaxed">
                  {businessInfo.address && <p className="mb-1">{businessInfo.address}</p>}
                  <p>
                    {businessInfo.phone && <span className="font-medium">{businessInfo.phone}</span>}
                    {businessInfo.phone && businessInfo.email && <span className="mx-2">•</span>}
                    {businessInfo.email && <span className="font-medium">{businessInfo.email}</span>}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-right print-invoice-number">
              <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Ledger</p>
              <p className="text-xs text-stone-600">
                <span className="font-semibold">Generated:</span> {generatedAt}
              </p>
            </div>
          </div>
        </div>

        <div className="print-divider" />

        {/* Date Range */}
        <div className="text-right text-xs text-stone-600 mb-4">
          Period: {fromDate} to {toDate}
        </div>

        {/* Client Info */}
        <div className="print-info-card mb-4">
          <p className="print-info-card-label">Client</p>
          <p className="print-info-card-value">{client.name}</p>
          <p className="text-xs text-stone-600 mt-1">
            {client.email || "No email"}
            {client.phone && ` | ${client.phone}`}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="print-info-card">
            <p className="print-info-card-label">Total Invoiced</p>
            <p className="print-info-card-value text-red-600">{formatCurrencyFull(summary.total_invoiced)}</p>
          </div>
          <div className="print-info-card">
            <p className="print-info-card-label">Total Paid</p>
            <p className="print-info-card-value text-emerald-600">{formatCurrencyFull(summary.total_paid)}</p>
          </div>
          <div className="print-info-card">
            <p className="print-info-card-label">Balance Due</p>
            <p className={`print-info-card-value ${summary.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {formatCurrencyFull(summary.current_balance)}
            </p>
          </div>
        </div>

        {/* Ledger Table */}
        <div className="mb-4">
          <div className="bg-stone-100 px-3 py-2 mb-2 border-l-4 border-amber-500">
            <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider">Ledger Entries</h3>
          </div>
          <table className="print-table">
            <thead>
              <tr>
                <th className="text-left w-16">Date</th>
                <th className="text-center w-20">Type</th>
                <th className="text-left">Description</th>
                <th className="text-right w-28">Debit (₹)</th>
                <th className="text-right w-28">Credit (₹)</th>
              </tr>
            </thead>
            <tbody>
              {entries.length > 0 ? (
                entries.map((entry, index) => (
                  <tr key={index}>
                    <td className="text-stone-700">{formatDate(entry.date)}</td>
                    <td className="text-center">
                      <span className={`text-xs px-2 py-1 rounded ${
                        entry.type === "invoice" 
                          ? "bg-red-50 text-red-700" 
                          : "bg-emerald-50 text-emerald-700"
                      }`}>
                        {entry.type === "invoice" ? "Invoice" : "Payment"}
                      </span>
                    </td>
                    <td className="font-medium text-stone-800">{entry.description}</td>
                    <td className="text-right text-red-600">{entry.debit}</td>
                    <td className="text-right text-emerald-600">{entry.credit}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center text-stone-500 py-8">
                    No ledger entries found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="print-footer">
          <div>
            <p className="font-bold text-sm text-stone-900">Thank you for choosing {businessInfo.name}!</p>
            <p className="text-xs text-stone-600 mt-1.5">
              <span className="font-semibold">For queries:</span> {businessInfo.email || "info@shalimar.com"} | {businessInfo.phone || "+91-XXXX-XXXX"}
            </p>
          </div>
          <div className="text-right text-xs text-stone-500">
            <p className="font-semibold text-amber-600">Generated on</p>
            <p className="mt-0.5">{new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
